/**
 * [Insider+] Join iframe — shown to non-subscribers. Subscribers are
 * redirected to /insider-plus-welcome (or back to the Discourse return URL).
 */

import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { INSIDER_PLUS_BASE_URL } from '../security/config';
import LoaderWrap from '../components/Loader/LoaderWrap';
import LoaderIcon from '../components/Loader/LoaderIcon';
import InsiderPlusIframeShell, {
  InsiderPlusPageWrapper,
} from '../components/InsiderPlus/InsiderPlusIframeShell';
import { selectHasInsiderPlusSubscription } from '../store/selectors/userSelectors';
import { INSIDER_PLUS_WELCOME_PAGE } from '../constants/pages';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import {
  INSIDER_PLUS_ENTRY_SOURCE,
  INSIDER_PLUS_RETURN_PATH,
} from '../constants/sessionStorage';
import { DISCOURSE_SOURCE } from '../util/helpers/insiderPlusEntryHelper';
import useInsiderPlusIframe from '../util/hooks/useInsiderPlusIframe';

const InsiderPlusPage = () => {
  const hasInsiderPlusSubscription = useSelector(
    selectHasInsiderPlusSubscription,
  );
  const history = useHistory();

  const buildIframeSrc = useCallback(() => `${INSIDER_PLUS_BASE_URL}/join`, []);

  const onNavigateUrl = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const {
    iframeRef,
    iframeSrc,
    iframeKey,
    isRecovering,
    authFailed,
    isRetrying,
    handleIframeLoad,
    handleRetry,
  } = useInsiderPlusIframe({ buildIframeSrc, onNavigateUrl });

  // Subscribed users belong on the welcome page (or back at the original
  // Discourse entry path so SSO completes), not the join iframe.
  useEffect(() => {
    if (hasInsiderPlusSubscription !== true) return;
    const entrySource = retrieveFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE);
    const returnPath = retrieveFromSessionStorage(INSIDER_PLUS_RETURN_PATH);
    const isDiscourseReturn =
      entrySource === DISCOURSE_SOURCE &&
      typeof returnPath === 'string' &&
      returnPath.startsWith('/forum-auth');
    removeFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE);
    removeFromSessionStorage(INSIDER_PLUS_RETURN_PATH);
    if (isDiscourseReturn) {
      history.replace(returnPath);
      return;
    }
    history.replace(INSIDER_PLUS_WELCOME_PAGE);
  }, [hasInsiderPlusSubscription, history]);

  // Wait for subscription state to resolve before mounting the join iframe,
  // so subscribed users never flash the join page before the redirect above.
  if (hasInsiderPlusSubscription !== false) {
    return (
      <InsiderPlusPageWrapper>
        <LoaderWrap>
          <LoaderIcon />
        </LoaderWrap>
      </InsiderPlusPageWrapper>
    );
  }

  return (
    <InsiderPlusIframeShell
      iframeRef={iframeRef}
      iframeSrc={iframeSrc}
      iframeKey={iframeKey}
      title="Insider Plus"
      dataCy="container-insider-plus-page"
      isRecovering={isRecovering}
      authFailed={authFailed}
      isRetrying={isRetrying}
      onIframeLoad={handleIframeLoad}
      onRetry={handleRetry}
    />
  );
};

export default InsiderPlusPage;
